import { type CommandProperties, createCommand } from '@atlassian/rovo-content-bridge-api';

export type PageContentCommandResult = {
	contentType: 'page';
	/**
	 * The ADF of the Confluence page.
	 */
	pageBody: any;
	/**
	 * The contentId of the Confluence page being interacted with
	 */
	contentId: string;
	/**
	 * The selected ADF in the Confluence page.
	 */
	selectionFragment: string;
	/**
	 * The selected ADF local ids in the Confluence page.
	 */
	selectionLocalIds: string;
	/**
	 * The current selection in markdown
	 */
	selectionMarkdown: string;
	/**
	 * Whether the page is in view mode (not editing)
	 */
	isViewMode?: boolean;
	/**
	 * Use the generic editor minion - this enables access to the editor minion
	 * without a product specific minion
	 */
	useGenericEditorSkill?: boolean;
};

export type WhiteboardContentCommandResult = {
	contentType: 'whiteboard';
	/**
	 * SVG representation of the whiteboard.
	 */
	svg: string;
	/**
	 * The contentId of the Confluence whiteboard being interacted with
	 */
	contentId: string;
	/**
	 * The WDF representation of the whiteboard.
	 */
	wdf: string;

	/**
	 * The title of the whiteboard.
	 */
	title: string;
	/**
	 * The URL of the whiteboard.
	 */
	url: string;
	/**
	 * The currently selected element ids.
	 */
	selectedElementIds: string[];
	/**
	 * Whether the whiteboard is in view/read only mode (not editing)
	 */
	isViewMode?: boolean;
};

type DatabaseContentCommandResult = {
	contentType: 'database';
	/**
	 * The contentId of the Confluence database being interacted with
	 */
	contentId: string;
	csv: string;
	title: string;
	url: string;
};

/**
 * Properties for the result of the read content command.
 */
export type ReadContentCommandResult =
	| PageContentCommandResult
	| WhiteboardContentCommandResult
	| DatabaseContentCommandResult;

/**
 * A command sent by a bridge API client to return the current content of a content type. It will have a different result type for each content type.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId} to return the current content of a content type.
 */
export const ReadContentCommand = createCommand<CommandProperties, ReadContentCommandResult>(
	'read-content-command',
);
